/**
 * @file Shows the player's facing marker on the player mesh.
 *
 * Runs after the render system, which has already copied `Transform.rotationY`
 * onto the mesh. The marker hangs off that mesh, so local zero means "where the
 * player is looking" with no per-frame allocation.
 */

import { System } from '../ecs/System.js';
import { Transform, Renderable } from '../components/index.js';
import { EXIT_ARROW_NAME } from '../rendering/ExitDirectionIndicator.js';

export class ExitDirectionSystem extends System {
  static priority = 101;

  constructor() {
    super();
    /** @type {import('three').Object3D | null} Mesh the arrow was resolved from. @private */
    this._object = null;
    /** @type {import('three').Object3D | null} @private */
    this._arrow = null;
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const arrow = this._resolveArrow();
    if (!arrow) return;

    const { player } = this.ctx;
    const transform = player?.alive ? player.get(Transform) : null;

    // No player, or the player is gone: neutral state.
    if (!transform) {
      arrow.visible = false;
      return;
    }

    arrow.rotation.y = 0;
    arrow.visible = true;
  }

  /**
   * Caches the arrow lookup, refreshing it whenever the player is handed a
   * different mesh (death, next floor, new run).
   *
   * Player death goes through `GameOverState`, which nulls `ctx.player` mid-frame
   * (from the `DIED` handler) before this system runs. Without hiding the previous
   * arrow here, the prefab would re-enter the mesh pool with a sticky
   * `visible = true` on the child — pool reset only clears the root mesh.
   *
   * @returns {import('three').Object3D | null}
   * @private
   */
  _resolveArrow() {
    const object = this.ctx.player?.get(Renderable)?.object ?? null;
    if (object !== this._object) {
      // Abandoning a mesh (player nulled, recycled, or swapped): force neutral.
      if (this._arrow) this._arrow.visible = false;
      this._object = object;
      this._arrow = object?.getObjectByName(EXIT_ARROW_NAME) ?? null;
    }
    return this._arrow;
  }

  /** @returns {void} */
  dispose() {
    this._object = null;
    this._arrow = null;
  }
}
